
import type { Dispatch } from 'redux';

import { isModerationNotificationDisplayed } from '../../notifications/actions';

import {
    GUM_PENDING,
    SET_AUDIO_AVAILABLE,
    SET_AUDIO_MUTED,
    SET_CAMERA_FACING_MODE,
    SET_CAMERA_ZOOM,
    SET_SCREENSHARE_MUTED,
    SET_SCREENSHARE_ZOOM,
    SET_VIDEO_AVAILABLE,
    SET_VIDEO_MUTED,
    SET_VIDEO_UNMUTE_PERMISSIONS,
    STORE_VIDEO_TRANSFORM,
    TOGGLE_CAMERA_FACING_MODE
} from './actionTypes';
import {
    CAMERA_FACING_MODE,
    CAMERA_ZOOM_MAX,
    CAMERA_ZOOM_MIN,
    MEDIA_TYPE,
    SCREENSHARE_MUTISM_AUTHORITY,
    VIDEO_MUTISM_AUTHORITY
} from './constants';

/**
 * Adds a bitmask flag to a flags value (without bitwise operators).
 *
 * @param {number} flags - The current flags.
 * @param {number} flag - The flag (power of 2) to add.
 * @returns {number} The updated flags.
 */
function _addFlag(flags, flag) {
    return flags + flag - (flags % (flag * 2) >= flag ? flag : 0);
}

/**
 * Removes a bitmask flag from a flags value (without bitwise operators).
 *
 * @param {number} flags - The current flags.
 * @param {number} flag - The flag (power of 2) to remove.
 * @returns {number} The updated flags.
 */
function _removeFlag(flags, flag) {
    return flags - (flags % (flag * 2) >= flag ? flag : 0);
}


/**
 * Action to adjust the availability of the local audio.
 *
 * @param {boolean} available - True if the local audio is to be marked as
 * available or false if the local audio is not available.
 * @returns {{
 *     type: SET_AUDIO_AVAILABLE,
 *     available: boolean
 * }}
 */
export function setAudioAvailable(available: boolean) {
    return {
        type: SET_AUDIO_AVAILABLE,
        available
    };
}

/**
 * Action to set the muted state of the local audio.
 *
 * @param {boolean} muted - True if the local audio is to be muted or false if
 * the local audio is to be unmuted.
 * @param {boolean} ensureTrack - True if we want to ensure that a new track is
 * created if missing.
 * @returns {{
 *     type: SET_AUDIO_MUTED,
 *     ensureTrack: boolean,
 *     muted: boolean
 * }}
 */
export function setAudioMuted(muted: boolean, ensureTrack: boolean = false) {
    return {
        type: SET_AUDIO_MUTED,
        ensureTrack,
        muted
    };
}

/**
 * Action to set the facing mode of the local camera.
 *
 * @param {CAMERA_FACING_MODE} cameraFacingMode - The camera facing mode to set.
 * @returns {{
 *     type: SET_CAMERA_FACING_MODE,
 *     cameraFacingMode: CAMERA_FACING_MODE
 * }}
 */
export function setCameraFacingMode(cameraFacingMode: CAMERA_FACING_MODE) {
    return {
        type: SET_CAMERA_FACING_MODE,
        cameraFacingMode
    };
}

/**
 * Sets the zoom factor applied to the local camera capture. Unlike the view
 * transform used for remote videos, this zoom is applied to the camera track
 * itself so remote participants see the same framed picture.
 *
 * @param {number} zoom - Requested zoom factor. 1 is no zoom.
 * @returns {{
 *     type: SET_CAMERA_ZOOM,
 *     zoom: number
 * }}
 */
export function setCameraZoom(zoom: number) {
    return {
        type: SET_CAMERA_ZOOM,
        zoom: _clampZoom(zoom)
    };
}

/**
 * Sets the zoom factor applied to the local screenshare capture. Web only in
 * practice: the shared screen is center-cropped before encoding so remote
 * participants see the same zoomed region.
 *
 * @param {number} zoom - Requested zoom factor. 1 is no zoom.
 * @returns {{
 *     type: SET_SCREENSHARE_ZOOM,
 *     zoom: number
 * }}
 */
export function setScreenshareZoom(zoom: number) {
    return {
        type: SET_SCREENSHARE_ZOOM,
        zoom: _clampZoom(zoom)
    };
}

/**
 * Clamps a requested capture zoom to the supported range.
 *
 * @param {number} zoom - Requested zoom factor.
 * @private
 * @returns {number}
 */
function _clampZoom(zoom) {
    const numeric = Number(zoom);

    return Number.isFinite(numeric)
        ? Math.min(CAMERA_ZOOM_MAX, Math.max(CAMERA_ZOOM_MIN, numeric))
        : CAMERA_ZOOM_MIN;
}

/**
 * Action to adjust the availability of the local video.
 *
 * @param {boolean} available - True if the local video is to be marked as
 * available or false if the local video is not available.
 * @returns {{
 *     type: SET_VIDEO_AVAILABLE,
 *     available: boolean
 * }}
 */
export function setVideoAvailable(available: boolean) {
    return {
        type: SET_VIDEO_AVAILABLE,
        available
    };
}

/**
 * Action to set the muted state of the local screenshare.
 *
 * @param {boolean} muted - True if the local screenshare is to be enabled or false otherwise.
 * @param {number} authority - The {@link SCREENSHARE_MUTISM_AUTHORITY} which is muting/unmuting the local screenshare.
 * @param {boolean} ensureTrack - True if we want to ensure that a new track is created if missing.
 * @returns {Function}
 */
export function setScreenshareMuted(
        muted,
        authority = SCREENSHARE_MUTISM_AUTHORITY.USER,
        ensureTrack = false) {
    return (dispatch, getState) => {
        const state = getState();

        // check for A/V Moderation when trying to unmute
        if (!muted && require('../../av-moderation/functions').shouldShowModeratedNotification(MEDIA_TYPE.SCREENSHARE, state)) {
            if (!isModerationNotificationDisplayed(MEDIA_TYPE.SCREENSHARE, state)) {
                ensureTrack && dispatch(require('../../av-moderation/actions').showModeratedNotification(MEDIA_TYPE.SCREENSHARE));
            }

            return;
        }

        const oldValue = state['features/base/media'].screenshare.muted;


        const newValue = muted ? _addFlag(oldValue, authority) : _removeFlag(oldValue, authority);

        return dispatch({
            type: SET_SCREENSHARE_MUTED,
            authority,
            ensureTrack,
            muted: newValue
        });
    };
}

/**
 * Action to disable/enable the video mute icon.
 *
 * @param {boolean} blocked - True if the video mute icon needs to be disabled.
 * @param {boolean|undefined} skipNotification - True if we want to skip showing the notification.
 * @returns {Function}
 */
export function setVideoUnmutePermissions(blocked: boolean, skipNotification = false) {
    return {
        type: SET_VIDEO_UNMUTE_PERMISSIONS,
        blocked,
        skipNotification
    };
}

/**
 * Action to set the muted state of the local video.
 *
 * @param {boolean} muted - True if the local video is to be muted or false if
 * the local video is to be unmuted.
 * @param {MEDIA_TYPE} mediaType - The type of media.
 * @param {number} authority - The {@link VIDEO_MUTISM_AUTHORITY} which is
 * muting/unmuting the local video.
 * @param {boolean} ensureTrack - True if we want to ensure that a new track is
 * created if missing.
 * @returns {Function}
 */
export function setVideoMuted(
        muted: boolean,
        mediaType: MEDIA_TYPE = MEDIA_TYPE.VIDEO,
        authority: number = VIDEO_MUTISM_AUTHORITY.USER,
        ensureTrack: boolean = false) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const oldValue = getState()['features/base/media'].video.muted;


        const newValue = muted ? _addFlag(oldValue, authority) : _removeFlag(oldValue, authority);

        return dispatch({
            type: SET_VIDEO_MUTED,
            authority,
            mediaType,
            ensureTrack,
            muted: newValue
        });
    };
}

/**
 * Creates an action to store the last video {@link Transform} applied to a
 * stream.
 *
 * @param {string} streamId - The ID of the stream.
 * @param {Object} transform - The {@code Transform} to store.
 * @returns {{
 *     type: STORE_VIDEO_TRANSFORM,
 *     streamId: string,
 *     transform: Object
 * }}
 */
export function storeVideoTransform(streamId: string, transform: Object) {
    return {
        type: STORE_VIDEO_TRANSFORM,
        streamId,
        transform
    };
}

/**
 * Toggles the camera facing mode. Most commonly, for example, mobile devices
 * such as phones have a front/user-facing and a back/environment-facing
 * cameras. In contrast to setCameraFacingMode, allows the toggling to be
 * optimally and/or natively implemented without the overhead of separate reads
 * and writes of the current/effective camera facing mode.
 *
 * @returns {{
 *     type: TOGGLE_CAMERA_FACING_MODE
 * }}
 */
export function toggleCameraFacingMode() {
    return {
        type: TOGGLE_CAMERA_FACING_MODE
    };
}


/**
 * Sets the GUM pending status from unmute and initial track creation operation.
 *
 * @param {Array<MediaType>} mediaTypes - An array with the media types that GUM is called with.
 * @param {IGUMPendingState} status - The GUM status.
 * @returns {{
 *     type: TOGGLE_CAMERA_FACING_MODE,
 *     mediaTypes: Array<MediaType>,
 *     status: IGUMPendingState
 * }}
 */
export function gumPending(mediaTypes, status) {
    return {
        type: GUM_PENDING,
        mediaTypes,
        status
    };
}
